import { useContext } from 'react';
import ServicesContext from '../../global/services/ServicesContext';
import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { mutateDocumentsKeys } from './mutateKeyFactory';
import { LockUnlockDocumentsInfoRequest } from '../../Model/requests/LockUnlockDocumentsInfoRequest';
import { LockUnlockResponse } from '../../Model/responseDtos/LockUnlockResponse';
import { useNotificationContext } from '../../global/hooks/useNotificationContext';
import { v4 as uuidv4 } from 'uuid';
import { useSearchContext } from '../search/SearchContext';
import { searchKeys } from '../search/queryKeyFactory';
import { useGlobalizationContext } from '../globalization/GlobalizationContext';
import { useTranslation } from '../globalization/useTranslation';

export function useUnlockDocuments(): UseMutationResult<
  LockUnlockResponse,
  unknown,
  { dto: LockUnlockDocumentsInfoRequest }
> {
  const servicesCtx = useContext(ServicesContext);
  const { addCenterNotification } = useNotificationContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const queryClient = useQueryClient();
  const { queryRequest } = useSearchContext();

  const unlockDocuments = (data: { dto: LockUnlockDocumentsInfoRequest }): Promise<LockUnlockResponse> => {
    if (servicesCtx?.documentsService && data.dto) {
      return servicesCtx.documentsService?.unlockDocuments(data.dto);
    }
    throw new Error('Services context not initialized');
  };

  return useMutation({
    mutationKey: mutateDocumentsKeys.unlockDocuments,
    mutationFn: (data: { dto: LockUnlockDocumentsInfoRequest }) => unlockDocuments(data),
    onSuccess: async data => {
      addCenterNotification({
        id: uuidv4(),
        title: translate('custom.notifications.notificationCenter.unlockDocument.unlockDocumentSuccessTitle'),
        message: translate('custom.notifications.notificationCenter.unlockDocument.unlockDocumentSuccessMessage', {
          changed: data.changed.length,
          total: data.changed.length + data.skipped.length
        }),
        timestamp: new Date(),
        type: 'success'
      });
      if (queryRequest) {
        await queryClient.invalidateQueries({ queryKey: searchKeys.filteredSearch(queryRequest) });
      }
    },
    onError: () => {
      addCenterNotification({
        id: uuidv4(),
        title: translate('custom.notifications.notificationCenter.unlockDocument.unlockDocumentFailedTitle'),
        message: translate('custom.notifications.notificationCenter.unlockDocument.unlockDocumentFailedMessage'),
        timestamp: new Date(),
        type: 'error'
      });
    }
  });
}
